import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useSEO } from "../lib/seo";
import { pushRecentlyViewed } from "../lib/recentlyViewed";
import {
  Star, Users, FileText, Clock, ShieldCheck, Heart, ChevronDown,
  CheckCircle2, BookOpen, Trophy, Timer, ArrowRight, Sparkles, LockKeyhole, PlayCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import CourseCard from "../components/CourseCard";
import CountdownTimer from "../components/CountdownTimer";
import RecentlyViewed from "../components/RecentlyViewed";
import { toast } from "sonner";

export default function CourseDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [access, setAccess] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/courses/${slug}`)
      .then((r) => { setCourse(r.data); pushRecentlyViewed(r.data); })
      .catch(() => setCourse(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!user || !course?.id) { setAccess(null); return; }
    api.get(`/me/access/${course.id}`).then((r) => setAccess(r.data)).catch(() => setAccess(null));
  }, [user, course?.id]);

  useSEO(
    course
      ? {
          title: course.title,
          description: course.subtitle || course.description,
          image: course.image_url,
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "Course",
              name: course.title,
              description: course.subtitle || course.description,
              provider: {
                "@type": "Organization",
                name: "BeforeExam",
                url: typeof window !== "undefined" ? window.location.origin : "",
              },
              aggregateRating: course.rating_count
                ? {
                    "@type": "AggregateRating",
                    ratingValue: course.rating_avg || 4.7,
                    reviewCount: course.rating_count,
                  }
                : undefined,
              offers: {
                "@type": "Offer",
                price: course.price,
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
              },
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: typeof window !== "undefined" ? window.location.origin : "" },
                { "@type": "ListItem", position: 2, name: "Marketplace", item: typeof window !== "undefined" ? `${window.location.origin}/marketplace` : "" },
                { "@type": "ListItem", position: 3, name: course.title },
              ],
            },
            ...(course.faq && course.faq.length
              ? [
                  {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    mainEntity: course.faq.map((f) => ({
                      "@type": "Question",
                      name: f.q,
                      acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                  },
                ]
              : []),
          ],
        }
      : { title: "Loading course…" },
  );

  const finalPrice = course ? Math.max(0.5, +(course.price * (1 - discount / 100)).toFixed(2)) : 0;

  const applyCoupon = async () => {
    if (!coupon) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon });
      setDiscount(data.percent_off);
      toast.success(`Coupon applied — ${data.percent_off}% off`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Invalid coupon");
    }
  };

  const buy = async () => {
    if (!user) {
      navigate("/login", { state: { from: { pathname: `/courses/${slug}` } } });
      return;
    }
    setPurchasing(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout/course", {
        course_id: course.id,
        origin_url: origin,
        coupon_code: coupon || null,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not start checkout");
      setPurchasing(false);
    }
  };

  const startPractice = (mode = "practice") => {
    navigate(`/practice/${course.id}/1?mode=${mode}`);
  };

  const enrollFree = async () => {
    if (!user) {
      navigate("/login", { state: { from: { pathname: `/courses/${slug}` } } });
      return;
    }
    setEnrolling(true);
    try {
      await api.post(`/me/enroll-free/${course.id}`);
      const { data } = await api.get(`/me/access/${course.id}`);
      setAccess(data);
      toast.success("Course unlocked — start practicing now");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not enroll");
    } finally {
      setEnrolling(false);
    }
  };

  const wishlistToggle = async () => {
    if (!user) return navigate("/login");
    try {
      const { data } = await api.post("/wishlist/toggle", { course_id: course.id });
      toast.success(data.action === "added" ? "Saved to wishlist" : "Removed from wishlist");
    } catch (e) {
      toast.error("Could not update wishlist");
    }
  };

  if (loading) return <div className="container-x py-20" data-testid="course-loading">Loading…</div>;
  if (!course) return <div className="container-x py-20"><h1 className="font-display text-3xl">Course not found</h1></div>;

  const discountPct = course.original_price > course.price
    ? Math.round((1 - course.price / course.original_price) * 100)
    : 0;

  return (
    <div data-testid="course-detail-page">
      {/* HERO */}
      <section className="border-b border-border bg-[color:var(--brand-cream-2)]">
        <div className="container-x py-12 grid lg:grid-cols-[1fr_400px] gap-10">
          <div>
            <Link to="/marketplace" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              ← Back to marketplace
            </Link>
            <div className="mt-4 flex flex-wrap gap-2">
              {course.bestseller && <span className="tag-pill bg-yellow-400 text-yellow-950">Bestseller</span>}
              {course.new_launch && <span className="tag-pill bg-[color:var(--brand-blue)] text-white">New launch</span>}
              {course.recently_updated && <span className="tag-pill bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">Updated 2026</span>}
            </div>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.1]">{course.title}</h1>
            <p className="mt-3 text-lg text-muted-foreground">{course.subtitle}</p>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
              <span className="flex items-center gap-1.5"><Star className="h-4 w-4 fill-yellow-500 stroke-yellow-500" /><b>{(course.rating_avg || 4.7).toFixed(1)}</b> ({course.rating_count || 0} reviews)</span>
              <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {(course.students_count || 0).toLocaleString()} enrolled</span>
              <span className="flex items-center gap-1.5"><FileText className="h-4 w-4" /> {course.test_count} mock exams · {course.question_count} questions</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {course.duration_minutes} min/test</span>
            </div>
          </div>

          <div className="lg:row-span-2">
            <div className="lg:sticky lg:top-24 rounded-2xl border border-border bg-card overflow-hidden shadow-xl shadow-blue-500/5">
              <div className="aspect-[16/10] bg-muted overflow-hidden relative">
                <img src={course.image_url} alt={course.title} className="h-full w-full object-cover" />
                {discountPct > 0 && (
                  <div className="absolute top-3 right-3 rounded-md bg-black/80 px-2 py-1 text-[11px] font-bold text-white">
                    −{discountPct}% off
                  </div>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-end gap-2">
                  <span className="font-display text-3xl">${finalPrice.toFixed(2)}</span>
                  {course.original_price > finalPrice && (
                    <span className="text-muted-foreground line-through">${course.original_price.toFixed(2)}</span>
                  )}
                  {discount > 0 && (
                    <span className="ml-2 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 text-xs font-bold">
                      -{discount}%
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">One-time payment · Lifetime access</div>

                <CountdownTimer className="mt-3" hours={24} label="Sale ends in" />

                {access?.has_access ? (
                  <div className="mt-4 space-y-2" data-testid="course-access-unlocked">
                    <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" /> Unlocked via {access.reason}
                    </div>
                    <Button
                      onClick={() => startPractice("practice")}
                      className="w-full h-12 bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-base font-semibold"
                      data-testid="start-practice-cta"
                    >
                      Start practice <PlayCircle className="ml-2 h-4 w-4" />
                    </Button>
                    <button onClick={() => startPractice("timed")} className="w-full btn-ghost" data-testid="start-timed-cta">
                      Timed mock mode
                    </button>
                  </div>
                ) : course.price <= 0 ? (
                  <Button
                    onClick={enrollFree}
                    disabled={enrolling}
                    className="mt-4 w-full h-12 bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-base font-semibold"
                    data-testid="free-enroll-cta"
                  >
                    {enrolling ? "Unlocking…" : <>Enroll free <ArrowRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={buy}
                      disabled={purchasing}
                      className="mt-4 w-full h-12 bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-base font-semibold"
                      data-testid="buy-cta"
                    >
                      {purchasing ? "Redirecting…" : <>Buy now <ArrowRight className="ml-2 h-4 w-4" /></>}
                    </Button>
                    <div className="mt-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground flex gap-2" data-testid="locked-course-note">
                      <LockKeyhole className="h-4 w-4 shrink-0" /> Practice access unlocks after purchase or active subscription.
                    </div>
                  </>
                )}
                <button onClick={wishlistToggle} className="mt-2 w-full inline-flex items-center justify-center gap-2 h-11 rounded-md border border-border hover:bg-accent text-sm font-semibold" data-testid="wishlist-button">
                  <Heart className="h-4 w-4" /> Add to wishlist
                </button>

                <div className="mt-4 flex gap-2">
                  <input
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                    placeholder="Coupon code"
                    className="flex-1 h-10 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-foreground"
                    data-testid="coupon-input"
                  />
                  <button onClick={applyCoupon} className="h-10 rounded-md border border-border px-4 text-xs font-semibold hover:bg-accent" data-testid="coupon-apply">
                    Apply
                  </button>
                </div>

                <div className="mt-5 space-y-2 text-sm">
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[color:var(--brand-success)]" /> {course.test_count} full-length mock exams</div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[color:var(--brand-success)]" /> {course.question_count} questions with explanations</div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[color:var(--brand-success)]" /> Adaptive analytics & weak-topic radar</div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[color:var(--brand-success)]" /> Lifetime access · Free updates</div>
                </div>

                <div className="mt-5 rounded-md border border-border p-3 flex items-center gap-3 text-xs">
                  <ShieldCheck className="h-5 w-5 text-[color:var(--brand-blue)]" />
                  <div>
                    <div className="font-semibold text-foreground">30-day money-back guarantee</div>
                    <div className="text-muted-foreground">No questions asked.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU LEARN */}
      <section className="container-x py-14 grid lg:grid-cols-[1fr_400px] gap-10">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight">What you'll master</h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-3">
            {(course.what_you_learn || []).map((w, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-[color:var(--brand-success)] shrink-0" />
                <span>{w}</span>
              </li>
            ))}
          </ul>

          <h2 className="mt-14 font-display text-2xl sm:text-3xl tracking-tight">Topics covered</h2>
          <div className="mt-5 flex flex-wrap gap-2">
            {(course.topics || []).map((t) => (
              <span key={t} className="rounded-full border border-border px-3 py-1 text-sm">{t}</span>
            ))}
          </div>

          <h2 className="mt-14 font-display text-2xl sm:text-3xl tracking-tight">How BeforeExam helps you pass</h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {[
              { i: <Timer className="h-5 w-5" />, t: "Real exam simulator", d: "Timed mode, full-screen focus, mark-for-review." },
              { i: <BookOpen className="h-5 w-5" />, t: "Detailed explanations", d: "Every question explained — not just answered." },
              { i: <Trophy className="h-5 w-5" />, t: "XP & streaks", d: "Stay consistent and beat exam-day jitters." },
            ].map((b) => (
              <div key={b.t} className="rounded-xl border border-border p-5">
                <div className="text-[color:var(--brand-blue)]">{b.i}</div>
                <div className="font-display text-lg mt-3">{b.t}</div>
                <div className="text-sm text-muted-foreground mt-1">{b.d}</div>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <h2 className="mt-14 font-display text-2xl sm:text-3xl tracking-tight">FAQ</h2>
          <div className="mt-4">
            {(course.faq || []).map((f, i) => (
              <div key={i} className="border-b border-border py-4">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="flex w-full items-center justify-between text-left">
                  <span className="font-display text-base pr-6">{f.q}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>}
              </div>
            ))}
          </div>

          {/* Reviews */}
          <h2 className="mt-14 font-display text-2xl sm:text-3xl tracking-tight">Student reviews</h2>
          <div className="mt-6 space-y-4">
            {(course.reviews || []).length === 0 && (
              <p className="text-sm text-muted-foreground">No reviews yet. Be the first to share your experience after passing!</p>
            )}
            {(course.reviews || []).map((r) => (
              <div key={r.id} className="rounded-xl border border-border p-5">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-[color:var(--brand-blue)] text-white flex items-center justify-center text-xs font-bold">
                    {r.user_name?.charAt(0) || "S"}
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{r.user_name || "Student"}</div>
                    <div className="flex gap-0.5">
                      {Array.from({ length: r.rating }).map((_, k) => <Star key={k} className="h-3 w-3 fill-yellow-500 stroke-yellow-500" />)}
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-sm">{r.comment}</p>
              </div>
            ))}
          </div>
        </div>
        <div /> {/* sticky right column already rendered above */}
      </section>

      {/* RELATED */}
      {course.related?.length > 0 && (
        <section className="container-x py-12 border-t border-border">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="h-4 w-4 text-[color:var(--brand-amber)]" />
            <h2 className="font-display text-2xl tracking-tight">People also bought</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {course.related.map((c) => <CourseCard key={c.id} course={c} compact />)}
          </div>
          <div className="mt-6">
            <Link to={`/compare?ids=${course.slug}`} className="inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--brand-amber-hover)] link-underline" data-testid="link-compare">
              Compare with another course <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      <RecentlyViewed excludeId={course.id} />
    </div>
  );
}
